import React, { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, Mail, Phone, Loader2, X, Info, Link2, AlertCircle, Upload } from "lucide-react";
import { UrlContext } from "../../contexts/UrlContext";
import Swal from "sweetalert2";

// UrlContext usually ends in /api/. Normalize either origin, /api or /api/.
const cvApiEndpoint = (base, route) => {
  const normalized = String(base || "").replace(/\/+$/, "");
  const apiBase = /\/api$/i.test(normalized) ? normalized : `${normalized}/api`;
  return `${apiBase}/${route}`;
};

// Upload via existing api_save_CV Graph storage path, with optional contact updates.
export default function CvContactPreviewModal({ open, file, expertID, expertName, existingEmail, existingPhone, onClose, onUploaded }) {
  const { API_URL } = useContext(UrlContext);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedPhone, setSelectedPhone] = useState("");
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (!open || !file || !expertID) return;
    let cancelled = false;
    const controller = new AbortController();
    setResult(null); setError(""); setSelectedEmail(""); setSelectedPhone("");
    setLoading(true);
    const data = new FormData();
    data.append("file", file);
    data.append("expertID", String(expertID));
    fetch(cvApiEndpoint(API_URL, "preview-consultant-cv-contacts"), {
      method: "POST", body: data, signal: controller.signal,
    }).then(async response => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status !== "success") throw new Error(payload.message || "Extraction failed");
      return payload;
    }).then(payload => { if (!cancelled) setResult(payload); })
      .catch(err => { if (!cancelled && err.name !== "AbortError") setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [open, file, expertID, API_URL]);

  const emails = result?.extracted?.emails || [];
  const phones = result?.extracted?.phones || [];
  const links = result?.extracted?.linkedin_links || [];
  const hasUpdates = Boolean(selectedEmail || selectedPhone);
  if (!open || typeof document === "undefined") return null;

  async function showNotice({ icon, title, text, timer }) {
    return Swal.fire({
      icon,
      title,
      text,
      showConfirmButton: false,
      showCancelButton: false,
      showCloseButton: false,
      timer,
      timerProgressBar: false,
      // Our modal uses z-[10050]. Ensure SweetAlert appears above it.
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = "11000";
      },
    });
  }

  async function uploadCv() {
    if (!file || !expertID || loading || uploading || !result) return;
    setUploading(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("expertID", String(expertID));
      if (selectedEmail) body.append("selectedEmail", selectedEmail);
      if (selectedPhone) body.append("selectedPhone", selectedPhone);

      const response = await fetch(cvApiEndpoint(API_URL, "upload-consultant-cv-with-contacts"), {
        method: "POST",
        body,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || "CV upload failed");
      }

      try { onUploaded?.(payload); } catch (callbackError) { console.error("CV uploaded, but UI refresh failed", callbackError); }
      onClose?.();
      await showNotice({
        icon: "success",
        title: payload.cvAlreadyExists ? "CV already exists" : "Success",
        text: payload.message,
        timer: 1500,
      });
    } catch (err) {
      await showNotice({
        icon: "error",
        title: "Unable to process CV",
        text: err.message || "CV upload failed",
        timer: 4000,
      });
    } finally {
      setUploading(false);
    }
  }

  // Toggleable single-choice buttons: selecting the same value again unselects it.
  const chooser = (kind, items, existing, selected, setSelected, Icon) => (
    <section className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#365561]"><Icon size={17}/>{kind}</div>
      <div className="mb-3 text-[11px] text-slate-500">Currently saved: <span className="font-semibold text-slate-700">{existing || "Not available"}</span></div>
      {items.length === 0 ? <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Not found in CV. Current value will remain unchanged.</p>
        : <div className="space-y-2">{items.map(item => {
            const checked = selected === item.value;
            return <button type="button" key={`${kind}-${item.value}`} disabled={uploading} aria-pressed={checked}
              onClick={() => setSelected(checked ? "" : item.value)}
              className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left transition ${checked ? "border-[#365561] bg-[#365561]/[0.07]" : "border-slate-200 hover:border-[#365561]/40"}`}>
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${checked ? "border-[#365561]" : "border-slate-400"}`}>
                {checked && <span className="h-2 w-2 rounded-full bg-[#365561]" />}
              </span>
              <span className="min-w-0 flex-1"><span className="block break-all text-xs font-semibold text-slate-800">{item.display || item.value}</span><span className="block text-[10px] text-slate-400">Page {item.page} · {item.source === "pdf_link" ? "PDF link" : "PDF text"}</span></span>
            </button>;
          })}</div>}
    </section>
  );

  return createPortal(<AnimatePresence><motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-[10050] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm" onMouseDown={e => { if (!uploading && e.target === e.currentTarget) onClose(); }}>
    <motion.div role="dialog" aria-modal="true" aria-label="Preview CV contact extraction" initial={{opacity:0,y:20,scale:.98}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,y:12,scale:.98}} transition={{type:"spring",stiffness:340,damping:30}} className="flex max-h-[92vh] w-full max-w-[900px] flex-col overflow-hidden rounded-[26px] border border-slate-200 bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_34%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_28px_90px_rgba(15,23,42,0.34)]">
      <header className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div><div className="text-[10px] font-bold uppercase tracking-widest text-[#365561]">CV contact extraction</div><h2 className="mt-1 text-xl font-bold text-slate-800">Review detected contact details</h2><p className="mt-1 text-xs text-slate-500">{expertName} · Expert ID {expertID}</p></div>
        <button type="button" aria-label="Close" disabled={uploading} className="rounded-lg p-2 text-slate-500 hover:bg-white disabled:opacity-50" onClick={onClose}><X size={20}/></button>
      </header>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3"><FileText size={18} className="text-[#365561]"/><span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800">{file?.name}</span><span className="text-[10px] text-slate-400">Ready to upload</span></div>
        {loading ? <div className="flex items-center justify-center gap-2 rounded-xl bg-white p-8 text-sm text-slate-600"><Loader2 className="animate-spin" size={19}/>Extracting contact details...</div> : null}
        {error ? <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"><AlertCircle size={18}/>{error}</div> : null}
        {result && <><div className="flex flex-wrap gap-2 text-[11px] text-slate-600"><span className="rounded-full bg-white px-3 py-1">{result.extracted.page_count} pages</span><span className="rounded-full bg-white px-3 py-1">{emails.length} email(s)</span><span className="rounded-full bg-white px-3 py-1">{phones.length} phone(s)</span><span className="rounded-full bg-white px-3 py-1">{result.extracted.ocr_pages.length} OCR page(s)</span></div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{chooser("Email", emails, existingEmail, selectedEmail, setSelectedEmail, Mail)}{chooser("Phone", phones, existingPhone, selectedPhone, setSelectedPhone, Phone)}</div>
          {links.length > 0 && <section className="rounded-xl bg-white p-3"><div className="mb-2 flex items-center gap-1.5 text-xs font-bold text-[#365561]"><Link2 size={14}/>LinkedIn links detected</div>{links.map(link => <p key={link.value} className="break-all text-xs text-slate-600">{link.value}</p>)}</section>}
          <div className="flex items-start gap-2 rounded-xl bg-[#365561]/[.07] p-3 text-xs text-slate-600"><Info size={16} className="mt-0.5 shrink-0 text-[#365561]"/><span>The CV will be uploaded to SharePoint if it does not already exist. {hasUpdates ? `Selected ${[selectedEmail && "email", selectedPhone && "phone"].filter(Boolean).join(" and ")} will be updated in the consultant profile while uploading.` : "Email and phone number will remain unchanged because nothing is selected."}</span></div>
        </>}
      </div>
      <footer className="flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-white/85 px-5 py-4">
        <button type="button" disabled={uploading} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50" onClick={onClose}>Cancel</button>
        <button type="button" disabled={loading || uploading || !result} className="inline-flex items-center gap-2 rounded-xl bg-[#365561] px-5 py-2 text-sm font-semibold text-white hover:bg-[#2e4a54] disabled:cursor-not-allowed disabled:opacity-50" onClick={uploadCv}>{uploading ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>} {uploading ? "Uploading..." : hasUpdates ? "Update & Upload" : "Upload CV"}</button>
      </footer>
    </motion.div>
  </motion.div></AnimatePresence>, document.body);
}
